/**
 * Centralized spreadsheet formula-injection (CSV Injection) sanitizer.
 *
 * Remediation for CSV Injection (OWASP A03:2021-Injection).
 *
 * User-supplied spreadsheet cell content (e.g. from uploaded Excel workbooks)
 * must be treated as inert plain text throughout the lifecycle (ingest -> store
 * -> export) so that no spreadsheet client (Excel / Sheets / LibreOffice) will
 * evaluate it as an executable formula.
 *
 * The sanitizer:
 *  - Detects dangerous formula lead characters: = + - @ TAB CR
 *  - Neutralizes them by prefixing a single quote (') so the value is rendered
 *    literally.
 *  - Is idempotent: applying it twice does not corrupt an already-safe value.
 *  - Normalizes leading zero-width / whitespace characters that could be used to
 *    bypass the lead-character check.
 */

/** Characters that, when leading a cell, can trigger formula evaluation. */
export const DANGEROUS_FORMULA_LEAD_CHARS: readonly string[] = ["=", "+", "-", "@", "\t", "\r"];

/** Neutralizing prefix applied to dangerous cell values. */
export const FORMULA_NEUTRALIZER_PREFIX = "'";

/**
 * Zero-width / invisible characters that can be used to smuggle a dangerous
 * lead character past a naive first-character check. These are stripped from
 * the start of a value before evaluation.
 *
 * IMPORTANT: TAB (\u0009) and CR (\u000D) are intentionally EXCLUDED here
 * because they are themselves dangerous formula lead characters and must be
 * neutralized rather than stripped.
 */
// NOSONAR: Intentionally matching control characters for CSV/formula security
const LEADING_INVISIBLE_CHARS_REGEX = /^[\u0000-\u0008\u000B\u000C\u000E-\u001F\u0020\u00A0\u200B-\u200D\uFEFF]+/;  // NOSONAR

/**
 * Returns true when the provided value would be interpreted as a formula by a
 * spreadsheet client (i.e. it begins with a dangerous lead character after
 * trimming invisible/whitespace characters).
 */
export const isFormulaInjection = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  const str = toSafeString(value).replace(LEADING_INVISIBLE_CHARS_REGEX, "");
  if (str.length === 0) return false;
  return DANGEROUS_FORMULA_LEAD_CHARS.includes(str.charAt(0));
};

/**
 * Neutralizes a single cell value so it can never be interpreted as a formula.
 *
 * - Non-string values are coerced to their string representation.
 * - Leading invisible characters are stripped to prevent bypasses.
 * - If the (cleaned) value starts with a dangerous lead character, a single
 *   quote prefix is added.
 * - Idempotent: a value already prefixed with the neutralizer is left unchanged.
 *
 * @param value Raw cell value (string, number, or coercible value).
 * @returns Plain-text, formula-safe string.
 */
export const sanitizeCell = (value: unknown): string => {
  if (value === null || value === undefined) return "";

  const raw = toSafeString(value);
  const cleaned = raw.replace(LEADING_INVISIBLE_CHARS_REGEX, "");

  if (cleaned.length === 0) return cleaned;

  if (cleaned.startsWith(FORMULA_NEUTRALIZER_PREFIX)) {
    return cleaned;
  }

  if (DANGEROUS_FORMULA_LEAD_CHARS.includes(cleaned.charAt(0))) {
    return `${FORMULA_NEUTRALIZER_PREFIX}${cleaned}`;
  }

  return cleaned;
};

const toSafeString = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null) {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return String(value);
};

/**
 * Sanitizes every cell in a row array.
 *
 * @param row Array of raw cell values.
 * @returns Array of formula-safe plain-text strings.
 */
export const sanitizeRow = (row: unknown[]): string[] => {
  if (!Array.isArray(row)) return [];
  return row.map((cell) => sanitizeCell(cell));
};

/**
 * Extracts a safe, plain-text string from an arbitrary ExcelJS cell value.
 *
 * ExcelJS may expose a formula cell as an object of the shape
 * `{ formula: string, result: unknown }` (and other rich-text/hyperlink
 * shapes). This helper discards any live `formula` and keeps only the cached
 * `result` / display text, then neutralizes it.
 *
 * @param cellValue Arbitrary ExcelJS cell value.
 * @returns Formula-safe plain-text string.
 */
export const sanitizeExcelCellValue = (cellValue: unknown): string => {
  if (cellValue === null || cellValue === undefined) return "";

  if (typeof cellValue === "object") {
    const obj = cellValue as Record<string, unknown>;

    // Formula cell: keep only the cached result (display text), drop the formula.
    if ("formula" in obj || "sharedFormula" in obj) {
      const result = "result" in obj ? obj.result : "";
      return sanitizeCell(result ?? "");
    }

    // Rich text cell: concatenate the visible text runs.
    if ("richText" in obj && Array.isArray(obj.richText)) {
      const text = (obj.richText as Array<{ text?: string }>).map((run) => run?.text ?? "").join("");
      return sanitizeCell(text);
    }

    // Hyperlink cell: keep only the visible text.
    if ("text" in obj) {
      return sanitizeCell(obj.text ?? "");
    }

    // Fallback: never emit a raw object; coerce and neutralize.
    return sanitizeCell(JSON.stringify(cellValue));
  }

  return sanitizeCell(cellValue);
};
